import { server } from './../constants';
import { httpClient } from './../utils/HttpClient';
import { base64 } from './../utils/utils';
import * as file from './../data/file';

/*______________
 *  Issue Data
 *==============
 */

/**
 * getCommentTypeList()
 * Detail: get status for access issue
 * @returns bool accessStatus
 */
export function getCommentTypeList(params) {
  return httpClient
    .get(`${server.ISSUE_URL}/getCommentTypeList`, { params })
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * accessible()
 * Detail: get status for access issue
 * @returns bool accessStatus
 */
export function accessible(params) {
  return httpClient
    .get(`${server.ISSUE_URL}/access`, { params })
    .then((result) => {
      return result.data.content.accessStatus;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * createId()
 *
 * Detail: create new issue id to get id and short id
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#create-issue-id
 */
export function createId() {
  return httpClient
    .get(`${server.ISSUE_URL}/createId`)
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}


/**
 * updateProcessStatus()
 * @param {object} props
 * @param {object} props.issue_id
 * @param {string} props.status_id
 * Method: PUT
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#set-issue-process-status
 * @returns
 */
export function updateProcessStatus({ issue_id, status_id }) {
  return httpClient
    .put(`${server.ISSUE_URL}/setProcessStatus`, { issue_id, status_id })
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * addIssue() create issue of user lv0
 * @param {object} props
 * @param {object} props.issue issue data object
 * @param {string} props.logMode default: issueCreate
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#insert-issue
 */
export function addIssue({ issue = {}, logMode = 'issueCreate' }) {
  return httpClient
    .post(`${server.ISSUE_URL}/save`, { issue, logMode })
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * updateIssue() update issue
 * @param {object} props
 * @param {object} props.issue issue object
 * @param {array} props.labels labels object, default: undefined
 * @param {array} props.dimensions dimensions object, default: undefined
 * @param {string} props.logMode default: issueEdit
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#insert-issue
 */
export function updateIssue({
  issue = {},
  labels = undefined,
  dimensions = undefined,
  logMode = 'issueEdit',
}) {
  return httpClient
    .put(`${server.ISSUE_URL}/save`, { issue, labels, dimensions, logMode })
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * forwardIssue() forward issue to new category / level / group
 * @param {*} props
 * @param {string} issue_id
 * @param {string} category_id,
 * @param {number} level,
 * @param {string} permissionGroupFilter = null,
 * @return
 */
export function forwardIssue({
  issue_id,
  category_id,
  level,
  permissionGroupFilter = null,
}) {
  return httpClient
    .put(`${server.ISSUE_URL}/forward`, {
      issue_id,
      category_id,
      level,
      permissionGroupFilter,
    })
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 *
 * @param {object} props
 * @param {string:GUID} props.issue_id
 * @param {number} props.vote 0 Unsatisfied | 1 Satisfied
 * @returns
 */
export function setVote({ issue_id = null, vote = null }) {
  if (issue_id && vote) {
    return httpClient
      .put(`${server.ISSUE_URL}/setVote`, {
        issue_id,
        vote,
      })
      .then((result) => {
        return result.data.content;
      })
      .catch((error) => {
        throw error;
      });
  } else {
    return () => {
      throw 'setVote issue id or vote id null';
    };
  }
}

/**
 * getList() get list issue
 * @param {object} params
 * @param {string} params.package base64 of props {lang, dateStart, dateEnd, category_id }
 * @return
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#getlist
 */
export function getList(params) {
  return httpClient
    .get(`${server.ISSUE_URL}/getList`, { params })
    .then((result) => {
      return result.data.content;
    })
    .catch((error) => {
      throw error;
    });
}

/**
 * getDetailCore() add options parameter,
 * @param {object} params
 * @param {object} params.package base64 of props {lang, id}
 * @param {object} options
 * @param {object} options.includedThumbnail default: true
 * @param {object} options.includedViewStatus default: false (included readOnlyStatus)
 * @return
 * Detail: get detail issue
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#getdetail
 */

async function getDetailCore(
  params,
  options /*legacy { includedThumbnail = true }*/
) {
  options = options || {};
  options.includedThumbnail = options.includedThumbnail || true;
  options.includedViewStatus = options.includedViewStatus || false;
  try {
    let respIssue = await httpClient.get(`${server.ISSUE_URL}/getDetail`, {
      params,
    });
    let issue = respIssue.data.content;
    let issue_id = issue.issues.id;

    // include thumbnail
    if (options.includedThumbnail) {
      let files = issue.issues.issueFiles
        ? issue.issues.issueFiles.split(',')
        : [];
      let thumbnails = await file.thumbnails({ issue_id, files });
      issue.issues.thumbnails = thumbnails;
    }

    // extraForStaff if get detail in mode staff we shall add veiwStatus automatic
    if (options.includedViewStatus) {
      let lang = 'TH';
      let dateStart = issue.issues.createDate.substr(0, 10);
      let dateEnd = dateStart;
      let category_id = issue.issues.category_id;
      let lists = await getList({
        package: base64.encodeObj({
          lang,
          mode: server.REQ_PARAM_MODE_STAFF,
          dateStart,
          dateEnd,
          category_id,
        }),
      });

      let thisIssueFromList = lists.find(
        (list) => list.issues.id === issue.issues.id
      );
      if (thisIssueFromList) {
        issue.viewStatus = thisIssueFromList.viewStatus;
        issue.readOnlyStatus = thisIssueFromList.readOnlyStatus;
      }
    }

    return issue;
  } catch (error) {
    throw error;
  }
}

/**
 * getDetail() v2 add options parameter,
 * @param {object} params
 * @param {object} params.package base64 of props {lang, id}
 * @param {object} options
 * @param {object} options.includedThumbnail default: true
 * @param {object} options.includedViewStatus default: false
 * @return
 * Detail: get detail issue
 * API Doc: /muit-iDev/pdpa-im-api/-/wikis/Transaction/Issue#getdetail
 */
export function getDetail(params, options) {
  return getDetailCore(params, options)
    .then((issue) => {
      return issue;
    })
    .catch((error) => {
      throw error;
    });
}
